// Precios vs competencia — de las series del backend a lo que dibuja la gráfica.
//
// El backend ya manda la historia armada (un punto por cambio, con su precio, si había stock y
// si el tramo es importado). Acá NO se reconstruye nada: sólo se acomodan los puntos a la forma
// de filas que pide recharts y se parte cada línea en hasta tres trazos, porque recharts no
// sabe cambiar de estilo a mitad de una línea:
//
//   <tienda>        tramo observado con stock     → trazo lleno
//   <tienda>__out   tramo sin stock               → trazo punteado
//   <tienda>__imp   historia importada del sitio  → trazo más claro
//
// Cada tramo va del punto i al i+1 con el precio y el estilo del punto i (línea escalonada).

export const STORE_LABELS = {
    enigmagames: 'Enigma',
    mgh: 'MGH',
    sparta: 'Sparta',
    xuruguay: 'XUruguay',
    saccum: 'SACCUM',
    bebemi: 'Bebemi',
    ludic: 'Ludic',
    febo: 'Febo',
    activemas: 'Active+',
};

const STORE_COLORS = {
    enigmagames: '#c4183c',
    mgh: '#007bff',
    sparta: '#17a2b8',
    xuruguay: '#6f42c1',
    saccum: '#fd7e14',
    bebemi: '#28a745',
    ludic: '#e83e8c',
    febo: '#795548',
    activemas: '#607d8b',
};
const FALLBACK_COLORS = ['#5a6169', '#20c997', '#6610f2', '#b8860b'];

export const storeLabel = (store) => STORE_LABELS[store] || String(store || '');

export const formatMoney = (value) => (value == null ? '—' : `$${Number(value).toLocaleString('es-UY', { maximumFractionDigits: 0 })}`);

export const formatDay = (value) => (value == null ? '—'
    : new Date(value).toLocaleDateString('es-UY', { day: '2-digit', month: '2-digit', year: '2-digit' }));

export const formatDateTime = (value) => (value == null ? '—'
    : new Date(value).toLocaleString('es-UY', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }));

const styleOf = (point) => {
    if (point.origin === 'imported') return 'imp';
    return point.inStock === false ? 'out' : 'in';
};
const SUFFIX = { in: '', out: '__out', imp: '__imp' };
const STYLE_NOTE = { in: '', out: ' (sin stock)', imp: ' (historia importada)' };

/**
 * @param {Array<{offerId:number, store:string, isOwn:boolean, points:Array<{t:string, price:number, inStock:boolean|null, origin:string}>}>} series
 * @returns {{ data: Array<object>, lines: Array<{key, base, store, label, name, color, isOwn, style, legend}> }}
 */
export function toChartData(series) {
    const usable = (series || []).filter((line) => Array.isArray(line.points) && line.points.length > 0);

    // Todas las fechas de todas las líneas: recharts corta una línea donde encuentra un hueco,
    // así que cada tramo se rellena en TODAS las filas que caen dentro de él.
    const times = Array.from(new Set(usable.flatMap((line) => line.points.map((point) => new Date(point.t).getTime()))))
        .sort((a, b) => a - b);
    const rows = new Map(times.map((t) => [t, { t }]));

    const lines = [];
    const seen = new Map();
    usable.forEach((line, index) => {
        // Dos publicaciones de la misma tienda en una ficha son dos líneas distintas.
        const repeat = (seen.get(line.store) || 0) + 1;
        seen.set(line.store, repeat);
        const base = line.isOwn ? 'enigma' : `${line.store}${repeat > 1 ? `_${repeat}` : ''}`;
        const label = line.isOwn ? 'Enigma' : `${storeLabel(line.store)}${repeat > 1 ? ` (${repeat})` : ''}`;
        const color = line.isOwn
            ? STORE_COLORS.enigmagames
            : (STORE_COLORS[line.store] || FALLBACK_COLORS[index % FALLBACK_COLORS.length]);

        const points = line.points.map((point) => ({ ...point, at: new Date(point.t).getTime(), style: styleOf(point) }));
        const used = new Set();
        for (let i = 0; i < points.length; i += 1) {
            const point = points[i];
            const key = `${base}${SUFFIX[point.style]}`;
            const until = i + 1 < points.length ? points[i + 1].at : point.at;
            used.add(point.style);
            for (const t of times) {
                if (t < point.at) continue;
                if (t > until) break;
                rows.get(t)[key] = point.price;
            }
        }

        // El trazo lleno va siempre primero: es el que lleva la leyenda.
        ['in', 'out', 'imp'].filter((style) => used.has(style)).forEach((style) => {
            lines.push({
                key: `${base}${SUFFIX[style]}`,
                base,
                store: line.store,
                label,
                name: `${label}${STYLE_NOTE[style]}`,
                color,
                isOwn: Boolean(line.isOwn),
                style,
            });
        });
        // Una sola entrada de leyenda por tienda, aunque nunca haya tenido un tramo "lleno".
        const first = lines.find((entry) => entry.base === base);
        if (first) first.legend = true;
    });

    return { data: times.map((t) => rows.get(t)), lines };
}

/** Qué se puede hacer con una publicación desde el detalle, según su condición. */
export function offerActions(offer) {
    if (offer == null) return [];
    if (offer.state === 'ignored') return ['unignore'];
    if (offer.state === 'unconfirmed') return ['assign', 'ignore'];
    if (offer.state === 'other') return ['assign'];
    // Confirmada: se puede ignorar; y si la asignó una persona, deshacer esa asignación.
    return offer.resolvedBy === 'manual' ? ['unassign', 'ignore'] : ['ignore'];
}

export const STATE_LABELS = {
    confirmed: 'Cuenta',
    unconfirmed: 'Edición sin confirmar',
    ignored: 'Ignorada',
    other: 'De otra edición',
};

/** "Está bien así" sólo tiene sentido sobre un producto caro que todavía no se aceptó. */
export const canAccept = (position) => Boolean(position && position.caro && !position.accepted);


export const ALERT_REASONS = { caro: 'Quedó caro', unico: 'Quedamos únicos con stock' };

/**
 * Un cambio de una tienda, en una frase: "Sparta bajó de $4.100 a $3.190".
 * @param {{store:string,type:string,oldPrice:?number,newPrice:?number,newInStock:?boolean}} change
 */
export function describeChange(change) {
    if (change == null) return '';
    const who = storeLabel(change.store);
    switch (change.type) {
        case 'price': {
            if (change.oldPrice == null) return `${who} quedó en ${formatMoney(change.newPrice)}`;
            const verb = Number(change.newPrice) < Number(change.oldPrice) ? 'bajó' : 'subió';
            return `${who} ${verb} de ${formatMoney(change.oldPrice)} a ${formatMoney(change.newPrice)}`;
        }
        case 'stock':
            return change.newInStock ? `${who} volvió a tener stock` : `${who} se quedó sin stock`;
        case 'appeared':
            return `${who} lo empezó a vender a ${formatMoney(change.newPrice)}`;
        case 'disappeared':
            return `${who} dejó de publicarlo`;
        case 'reappeared':
            return `${who} volvió a publicarlo a ${formatMoney(change.newPrice)}`;
        default:
            return `${who} cambió`;
    }
}

/**
 * El "por qué" de una fila marcada: "Quedó caro: Sparta bajó de $4.100 a $3.190 y 1 cambio más".
 * Null si el producto no tiene avisos sin ver.
 */
export function alertSummary(row, max = 2) {
    const reasons = (row && row.alertReasons) || [];
    if (reasons.length === 0) return null;
    const title = reasons.map((reason) => ALERT_REASONS[reason] || reason).join(' y ');
    const changes = (row.alertChanges || []);
    if (changes.length === 0) return title;
    const rest = changes.length - max;
    const shown = changes.slice(0, max).map(describeChange).join('; ');
    return `${title}: ${shown}${rest > 0 ? ` y ${rest} ${rest === 1 ? 'cambio más' : 'cambios más'}` : ''}`;
}

/** Etiquetas de una fila de la lista, en el orden en que se muestran. */
export function rowBadges(row) {
    const badges = [];
    if (row.unseenAlerts > 0) badges.push({ text: 'nuevo', kind: 'danger' });
    if (row.unico) badges.push({ text: 'único con stock', kind: 'success' });
    if (row.sinStockPropio) badges.push({ text: 'sin stock nuestro', kind: 'secondary' });
    if (row.accepted) badges.push({ text: 'diferencia aceptada', kind: 'light' });
    if (row.unconfirmed > 0) {
        badges.push({ text: `${row.unconfirmed} ${row.unconfirmed === 1 ? 'publicación' : 'publicaciones'} sin confirmar`, kind: 'warning' });
    }
    return badges;
}

/** El texto de la advertencia de la cabecera, o null si la lectura está al día. */
export function statusWarning(status) {
    if (status == null) return null;
    if (status.warning === 'never') return 'Todavía no se leyó el comparador. La lectura se corre a pedido desde la computadora del local.';
    if (status.warning === 'incomplete') {
        const detalle = status.lastRun && status.lastRun.error ? ` (${status.lastRun.error})` : '';
        return `La última lectura no llegó a completarse${detalle}. Lo que ves puede estar desactualizado.`;
    }
    if (status.warning === 'stale') return 'La última lectura completa tiene más de 48 horas. Lo que ves puede estar desactualizado.';
    return null;
}
